<template>
  <div class="flex flex-col">
    <div class="text-static-default-hi" data-qa="productCardPrice">
      <p
        v-if="dueTodayPrice"
        class="label-medium-bold"
        :class="{ 'text-static-success-hi': !!deal }"
      >
        {{ dueTodayPrice }}
      </p>
      <p
        class="label-medium-bold"
        :class="{ 'text-static-success-hi': !!deal }"
      >
        <span class="sr-only">{{ i18n(translations.priceSRLabel) }}</span>
        <ProductPriceHighlighted :price="highlightedPrice" />
        {{ highlightedPriceLabel }}
      </p>
      <div class="space-y-4">
        <p v-if="dealInfo" class="label-medium-bold text-static-success-hi">
          {{ dealInfo }}
          <ProductPriceTooltip
            v-if="deal?.type === 'flash_sales'"
            icon-class="align-text-bottom"
            :label="i18n(translations.moreInfoSRLabel)"
          >
            {{ i18n(translations.flashSalesDiscountTooltip) }}
          </ProductPriceTooltip>
        </p>
        <p v-if="lastLowestPrice" class="label-medium text-static-default-low">
          {{ lastLowestPrice }}
          <ProductPriceTooltip
            v-if="sellerFlashSalesTooltip"
            icon-class="align-top"
            :label="i18n(translations.moreInfoSRLabel)"
          >
            {{ sellerFlashSalesTooltip }}
          </ProductPriceTooltip>
        </p>
        <p
          v-else-if="showNewPriceComparison"
          class="label-medium text-static-default-low"
        >
          <span aria-hidden="true">
            <s>{{ i18n.price(newPrice!) }}</s>
            {{ i18n(translations.newKeyword) }}
          </span>
          <span class="sr-only">{{
            i18n(translations.newPriceSRLabel, { price: i18n.price(newPrice!) })
          }}</span>
        </p>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import type { Price } from '@backmarket/http-api/src/api-models/Price'
import type { Deal } from '@backmarket/nuxt-layer-cms/products/algolia/types'
import { getSellerFlashSalesPeriod } from '@backmarket/nuxt-layer-deals/getSellerFlashSalesPeriod'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { calculateReduction } from '@backmarket/utils/math/calculateReduction'

import translations from './ProductPrice.translations'
import ProductPriceHighlighted from './components/ProductPriceHighlighted.vue'
import ProductPriceTooltip from './components/ProductPriceTooltip.vue'

const { price, newPrice, afterTradeinPrice, deal } = defineProps<{
  price: Price
  newPrice?: Price
  afterTradeinPrice?: Price
  deal?: Deal
}>()

const i18n = useI18n()
const locale = useI18nLocale()

const showNewPriceComparison = computed(() => {
  if (!newPrice) return false
  // Flash sales already show the discounted trade-in price — no need to show "new" price comparison
  if (afterTradeinPrice && deal?.type === 'flash_sales') return false

  return true
})

const highlightedPrice = computed(() => {
  const activePrice = afterTradeinPrice ?? price
  if (deal?.type !== 'flash_sales') return i18n.price(activePrice)

  const discountedPrice = calculateReduction(activePrice, deal.discount)
  if (!discountedPrice) return i18n.price(activePrice)

  const clampedAmount = Math.max(0, parseFloat(discountedPrice.amount))

  return i18n.price({ ...discountedPrice, amount: clampedAmount.toString() })
})

const highlightedPriceLabel = computed(() => {
  if (afterTradeinPrice) return i18n(translations.afterTradin)

  return null
})

const dueTodayPrice = computed(() => {
  if (!afterTradeinPrice) return null

  if (deal?.type === 'flash_sales') {
    const reduction = calculateReduction(price, deal.discount)

    return reduction
      ? `${i18n.price(reduction)} ${i18n(translations.dueToday)}`
      : null
  }

  return `${i18n.price(price)} ${i18n(translations.dueToday)}`
})

const dealInfo = computed(() => {
  if (deal?.type === 'flash_sales') {
    return i18n(translations.flashSalesDiscount, {
      discount: `${i18n.price(deal.discount, {
        trailingZeroDisplay: 'stripIfInteger',
      })}`,
    })
  }
  if (deal?.type === 'seller_flash_sales' && !afterTradeinPrice) {
    const reduction = calculateReduction(deal.percentage.minimum_price, price)

    return reduction
      ? i18n(translations.sellerFlashSalesDiscount, {
          price: i18n.price(reduction),
        })
      : null
  }

  return null
})

const lastLowestPrice = computed(() => {
  if (deal?.type === 'seller_flash_sales') {
    return i18n(translations.lastLowestPrice, {
      price: i18n.price(deal.percentage.minimum_price),
    })
  }

  return null
})

const sellerFlashSalesTooltip = computed(() => {
  if (deal?.type !== 'seller_flash_sales') return null

  const { startDate, endDate } = getSellerFlashSalesPeriod(new Date())
  const dateFormat: Intl.DateTimeFormatOptions = {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  }

  return i18n(translations.sellerFlashSalesDiscountTooltip, {
    start_date: startDate.toLocaleDateString(locale, dateFormat),
    end_date: endDate.toLocaleDateString(locale, dateFormat),
  })
})
</script>
